import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { MODELS, MODEL_KEYS, ModelChip, ModelBadge } from "../components/ModelBadge";
import Markdown from "../components/Markdown";
import { Plus, Trash2, Bot, Send, Sparkles, ArrowLeft } from "lucide-react";

export default function Agents() {
  const [params] = useSearchParams();
  const runAgentId = params.get("id");
  const runConvId = params.get("c");
  const isRunning = params.has("id") || params.has("c") || window.location.pathname.endsWith("/run");

  const [agents, setAgents] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "",
    description: "",
    emoji: "🤖",
    system_prompt: "",
    default_model: "deepseek",
  });

  useEffect(() => {
    (async () => {
      const [a, t] = await Promise.all([api.get("/agents"), api.get("/agents/templates")]);
      setAgents(a.data.agents || []);
      setTemplates(t.data.templates || []);
    })();
  }, []);

  const applyTemplate = (t) => setForm({ ...t });

  const save = async () => {
    if (!form.name || !form.system_prompt) return;
    setBusy(true);
    try {
      const { data } = await api.post("/agents", form);
      setAgents((a) => [data, ...a]);
      setForm({ name: "", description: "", emoji: "🤖", system_prompt: "", default_model: "deepseek" });
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this agent?")) return;
    await api.delete(`/agents/${id}`);
    setAgents((a) => a.filter((x) => x.agent_id !== id));
  };

  if (isRunning) {
    return <AgentRun agentId={runAgentId} convId={runConvId} agents={agents} />;
  }

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="agents-page">
      <header className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Bot size={26} strokeWidth={2.5} />
          <h1 className="font-heading text-3xl font-bold">Quick Agents</h1>
        </div>
        <p className="text-slate-500">Fast, single-model AI personas. For a full workforce with persona + memory + brain swapping, <Link to="/workforce" className="underline font-bold text-slate-900">visit Workforce</Link>.</p>
      </header>

      <div className="grid lg:grid-cols-[1fr_1fr] gap-6">
        {/* Builder */}
        <div className="brut-card p-6">
          <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
            <Sparkles size={20} strokeWidth={2.5} /> Build a new agent
          </h2>

          {templates.length > 0 && (
            <div className="mb-5">
              <div className="text-xs uppercase font-bold tracking-widest text-slate-500 mb-2">Quick start</div>
              <div className="flex flex-wrap gap-2" data-testid="templates">
                {templates.map((t, i) => (
                  <button
                    key={i}
                    data-testid={`template-${i}`}
                    onClick={() => applyTemplate(t)}
                    className="px-3 py-1.5 rounded-full border-2 border-slate-900 bg-white text-sm font-heading font-semibold hover:bg-amber-100 transition-colors"
                  >
                    <span className="mr-1">{t.emoji}</span>
                    {t.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div className="flex gap-3">
              <input
                data-testid="agent-emoji-input"
                value={form.emoji}
                onChange={(e) => setForm({ ...form, emoji: e.target.value })}
                className="brut-input w-20 text-center text-2xl"
                maxLength={2}
              />
              <input
                data-testid="agent-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="brut-input flex-1"
                placeholder="Agent name (e.g., Code Reviewer)"
              />
            </div>
            <input
              data-testid="agent-description-input"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="brut-input"
              placeholder="Short description"
            />
            <textarea
              data-testid="agent-prompt-input"
              value={form.system_prompt}
              onChange={(e) => setForm({ ...form, system_prompt: e.target.value })}
              className="brut-input resize-none"
              rows={6}
              placeholder="System prompt — define personality, expertise, behavior..."
            />
            <div>
              <div className="text-xs uppercase font-bold tracking-widest text-slate-500 mb-2">Default model</div>
              <div className="flex gap-2 flex-wrap">
                {MODEL_KEYS.map((k) => (
                  <ModelChip key={k} id={k} selected={form.default_model === k} onClick={() => setForm({ ...form, default_model: k })} />
                ))}
              </div>
            </div>
            <button
              data-testid="agent-save-btn"
              onClick={save}
              disabled={busy || !form.name || !form.system_prompt}
              className="brut-btn w-full"
            >
              <Plus size={16} strokeWidth={2.5} /> {busy ? "Saving..." : "Save agent"}
            </button>
          </div>
        </div>

        {/* List */}
        <div>
          <h2 className="font-heading text-xl font-bold mb-4">Your collection</h2>
          {agents.length === 0 ? (
            <div className="brut-card p-8 text-center">
              <div className="text-5xl mb-3 float-y inline-block">🤖</div>
              <div className="font-heading text-lg font-bold mb-1">No agents yet</div>
              <div className="text-sm text-slate-500">Use a template above to get started fast.</div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="agents-list">
              {agents.map((a) => (
                <div key={a.agent_id} className="brut-card p-4 flex gap-3 items-start">
                  <div className="text-3xl">{a.emoji}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-heading font-bold text-lg">{a.name}</div>
                      <ModelBadge id={a.default_model} size="sm" />
                    </div>
                    <div className="text-sm text-slate-500 truncate">{a.description}</div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Link
                      to={`/agents/run?id=${a.agent_id}`}
                      data-testid={`run-agent-${a.agent_id}`}
                      className="brut-btn brut-btn-outline text-sm px-3 py-1.5"
                    >
                      Run →
                    </Link>
                    <button
                      data-testid={`delete-agent-${a.agent_id}`}
                      onClick={() => del(a.agent_id)}
                      className="text-slate-400 hover:text-red-500 self-end"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AgentRun({ agentId, convId, agents }) {
  const [agent, setAgent] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [conversationId, setConversationId] = useState(convId || null);
  const [model, setModel] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      if (convId) {
        const { data } = await api.get(`/conversations/${convId}`);
        setMessages(data.messages || []);
        setConversationId(convId);
        if (data.conversation?.agent_id) {
          const a = agents.find((x) => x.agent_id === data.conversation.agent_id);
          if (a) { setAgent(a); setModel(a.default_model); }
        }
      } else if (agentId) {
        const a = agents.find((x) => x.agent_id === agentId);
        if (a) { setAgent(a); setModel(a.default_model); }
      }
    })();
  }, [agentId, convId, agents]);

  const send = async () => {
    if (!input.trim() || sending || !agent) return;
    const prompt = input;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: prompt }]);
    setSending(true);
    try {
      const { data } = await api.post("/agents/run", {
        agent_id: agent.agent_id,
        message: prompt,
        conversation_id: conversationId,
        model,
      });
      if (!conversationId) setConversationId(data.conversation_id);
      const r = data.response;
      setMessages((m) => [...m, { role: "assistant", content: r.content || `⚠️ ${r.error}`, model }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: `⚠️ ${e.response?.data?.detail || e.message}`, model, error: true }]);
    } finally {
      setSending(false);
    }
  };

  if (!agent) {
    return (
      <div className="p-8 text-center text-slate-500">
        <Link to="/agents" className="brut-btn brut-btn-outline inline-flex"><ArrowLeft size={14} /> Back</Link>
        <div className="mt-6">Loading agent…</div>
      </div>
    );
  }

  const m = MODELS[model || agent.default_model];

  return (
    <div className="flex flex-col h-screen" data-testid="agent-run-page">
      <header className="px-6 py-4 border-b-2 border-slate-900 bg-white flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Link to="/agents" className="text-slate-500 hover:text-slate-900"><ArrowLeft size={18} /></Link>
          <div className="text-3xl">{agent.emoji}</div>
          <div>
            <h1 className="font-heading text-xl font-bold">{agent.name}</h1>
            <p className="text-xs text-slate-500">{agent.description}</p>
          </div>
        </div>
        <div className="flex gap-1.5">
          {MODEL_KEYS.map((k) => (
            <ModelChip key={k} id={k} selected={(model || agent.default_model) === k} onClick={() => setModel(k)} />
          ))}
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-3xl mx-auto space-y-4">
          {messages.length === 0 && (
            <div className="text-center py-12">
              <div className="text-6xl mb-3 float-y inline-block">{agent.emoji}</div>
              <div className="font-heading text-xl font-bold">{agent.name} is ready</div>
              <div className="text-sm text-slate-500 max-w-md mx-auto mt-1">
                Powered by <ModelBadge id={model || agent.default_model} size="sm" />
              </div>
            </div>
          )}
          {messages.map((msg, i) => (
            msg.role === "user" ? (
              <div key={i} className="flex justify-end">
                <div className="max-w-[80%] bg-slate-900 text-white rounded-2xl rounded-br-sm px-4 py-3">{msg.content}</div>
              </div>
            ) : (
              <div key={i} className="flex justify-start">
                <div
                  className="max-w-[85%] border-2 rounded-2xl rounded-bl-sm px-4 py-3"
                  style={{ backgroundColor: m.bg, borderColor: m.border }}
                >
                  <Markdown text={msg.content} />
                </div>
              </div>
            )
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="border-2 rounded-2xl px-4 py-3 flex gap-1.5" style={{ backgroundColor: m.bg, borderColor: m.border }}>
                <span className="w-2 h-2 rounded-full dot-blink" style={{ backgroundColor: m.border }} />
                <span className="w-2 h-2 rounded-full dot-blink" style={{ backgroundColor: m.border, animationDelay: "0.2s" }} />
                <span className="w-2 h-2 rounded-full dot-blink" style={{ backgroundColor: m.border, animationDelay: "0.4s" }} />
              </div>
            </div>
          )}
        </div>
      </div>

      <footer className="border-t-2 border-slate-900 bg-white p-4">
        <div className="max-w-3xl mx-auto flex items-end gap-3">
          <textarea
            data-testid="agent-input"
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={`Talk to ${agent.name}…`}
            className="brut-input flex-1 resize-none max-h-32"
            style={{ minHeight: 48 }}
          />
          <button
            data-testid="agent-send-btn"
            onClick={send}
            disabled={!input.trim() || sending}
            className="brut-btn"
            style={{ backgroundColor: m.color, borderColor: m.border }}
          >
            <Send size={16} strokeWidth={2.5} /> Send
          </button>
        </div>
      </footer>
    </div>
  );
}
